import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ConfigService } from '../../../services/config.service';
import { MigrationResult, NotesAzureUserMapping } from '../../../models/notesToAzureUserMigration';
import {
  InvalidUserIdMatchWithAllMatches,
  ApplySingleMatchPayload,
  GraphMatchOptions
} from '../../../models/manualGraphMatches';

export interface ManualTriggerResponse {
  success: boolean;
  message: string;
  timestamp: string;
  processId?: string;
  statistics?: {
    successCount: number;
    failureCount: number;
    durationSeconds: number;
  };
  errors?: string[];
}

@Injectable({ providedIn: 'root' })
export class ManualTriggersService {
  private httpClient: HttpClient = inject(HttpClient);
  private configService: ConfigService = inject(ConfigService);

  private getHost(): string {
    return this.configService.getConfiguration().apiHost;
  }

  triggerOrganizationImport(): Observable<ManualTriggerResponse> {
    return this.httpClient.post<ManualTriggerResponse>(
      this.getHost() + '/api/ManualImportOrganizationsFromLakehouse',
      {}
    );
  }

  triggerRecommendationTitleMigration(): Observable<ManualTriggerResponse> {
    return this.httpClient.post<ManualTriggerResponse>(this.getHost() + '/api/MigrateAllRecommendationTitles', {});
  }

  triggerUserDisplayNameMigration(mappings: NotesAzureUserMapping[]): Observable<MigrationResult> {
    return this.httpClient.post<MigrationResult>(this.getHost() + '/api/user/notes-to-azure', mappings);
  }

  exportInvalidUserIds(): Observable<Blob> {
    return this.httpClient.get(this.getHost() + '/api/user/invalid-ids-export', { responseType: 'blob' });
  }

  exportInvalidUserIdsStatistics(): Observable<Blob> {
    return this.httpClient.get(this.getHost() + '/api/user/invalid-ids-statistics-export', { responseType: 'blob' });
  }

  getManualGraphMatches(options?: GraphMatchOptions): Observable<InvalidUserIdMatchWithAllMatches[]> {
    let url = this.getHost() + '/api/user/manual-graph-matches';
    if (options) {
      const params = new URLSearchParams();
      if (options.nameMatchingStrategy !== undefined) {
        params.append('nameMatchingStrategy', options.nameMatchingStrategy.toString());
      }
      if (options.emailDomainFilter) {
        params.append('emailDomainFilter', options.emailDomainFilter);
      }
      if (params.toString()) {
        url += '?' + params.toString();
      }
    }
    return this.httpClient.get<InvalidUserIdMatchWithAllMatches[]>(url);
  }

  applySingleGraphMatch(payload: ApplySingleMatchPayload): Observable<MigrationResult> {
    return this.httpClient.post<MigrationResult>(this.getHost() + '/api/user/apply-single-graph-match', payload);
  }

  applyGraphMatches(options?: GraphMatchOptions): Observable<MigrationResult> {
    return this.httpClient.post<MigrationResult>(this.getHost() + '/api/user/apply-graph-matches', options || {});
  }

  exportInvalidUserIdsWithMatches(options?: GraphMatchOptions): Observable<Blob> {
    let url = this.getHost() + '/api/user/invalid-ids-with-matches-export';
    if (options) {
      const params = new URLSearchParams();
      if (options.nameMatchingStrategy !== undefined) {
        params.append('nameMatchingStrategy', options.nameMatchingStrategy.toString());
      }
      if (options.emailDomainFilter) {
        params.append('emailDomainFilter', options.emailDomainFilter);
      }
      if (params.toString()) {
        url += '?' + params.toString();
      }
    }
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  triggerRecommendationStateUpdate(): Observable<ManualTriggerResponse> {
    return this.httpClient.post<ManualTriggerResponse>(this.getHost() + '/api/recommendation-states/update', {});
  }
}
